import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { TripFinance } from '../lib/tripAtlas';

// trip_runs, property_open_items, trip_external_links and the deal-import
// tables are newer than database.types.ts; regenerate types after
// `supabase db reset` and drop these casts.
type Result<T> = Promise<{ data: T | null; error: { message: string } | null }>;
type Query<T> = Result<T> & {
  eq: (c: string, v: string) => Query<T>;
  in: (c: string, v: string[]) => Query<T>;
  order: (c: string, o?: { ascending?: boolean; nullsFirst?: boolean }) => Query<T>;
  limit: (n: number) => Query<T>;
};
type Untyped = {
  from: (t: string) => {
    select: (cols: string) => Query<unknown[]>;
    insert: (row: Record<string, unknown>) => Result<unknown>;
    update: (patch: Record<string, unknown>) => { eq: (c: string, v: string) => Result<unknown> };
  };
  rpc: (fn: string, args: Record<string, unknown>) => Result<unknown>;
};
const db = supabase as unknown as Untyped;

function rethrow(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

// ─── Runs ────────────────────────────────────────────────────────────────────

export interface RunStop {
  trip_id: string;
  project_number: string;
  stop: number | null;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  start_date: string | null;
  end_date: string | null;
  location_id: string;
  location_name: string | null;
  open_items: number;
}

export interface TripRun {
  run_id: string;
  run_number: string;
  title: string;
  lead_technician_id: string | null;
  lead_technician_name: string | null;
  notes: string | null;
  created_at: string;
  start_date: string | null;
  end_date: string | null;
  stop_count: number;
  completed_count: number;
  cancelled_count: number;
  in_progress_count: number;
  run_state: 'empty' | 'planned' | 'underway' | 'done';
  stops: RunStop[];
}

export const RUNS_KEY = ['admin-trip-runs'] as const;

export function useTripRuns() {
  return useQuery({
    queryKey: RUNS_KEY,
    queryFn: async () => {
      const { data, error } = await db
        .from('trip_run_overview')
        .select('*')
        .order('start_date', { ascending: false, nullsFirst: false });
      rethrow(error);
      return (data ?? []) as TripRun[];
    },
    staleTime: 60_000,
  });
}

export interface PlannedStop {
  location_id: string;
  start_date: string;
  end_date: string;
  scope: string;
}

export function useCreateRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { title: string; leadTechnicianId: string | null; notes: string; stops: PlannedStop[] }) => {
      const { data, error } = await db.rpc('create_trip_run', {
        p_title: args.title,
        p_stops: args.stops,
        p_lead_technician_id: args.leadTechnicianId,
        p_notes: args.notes || null,
      });
      rethrow(error);
      return data as string;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: RUNS_KEY }),
        qc.invalidateQueries({ queryKey: ['admin-field-trips'] }),
        qc.invalidateQueries({ queryKey: ['admin-trip-billing'] }),
      ]),
  });
}

// ─── Open items ──────────────────────────────────────────────────────────────

export interface OpenItem {
  id: string;
  location_id: string;
  title: string;
  detail: string | null;
  priority: 'low' | 'medium' | 'high';
  status: 'open' | 'resolved' | 'wont_fix';
  opened_at: string;
  resolved_at: string | null;
  resolution: string | null;
  opened_trip: { id: string; project_number: string } | null;
  resolved_trip: { id: string; project_number: string } | null;
  opened_by: { full_name: string | null } | null;
  resolved_by: { full_name: string | null } | null;
}

const OPEN_ITEM_COLUMNS = `
  id, location_id, title, detail, priority, status, opened_at, resolved_at, resolution,
  opened_trip:field_trips!property_open_items_opened_trip_id_fkey(id, project_number),
  resolved_trip:field_trips!property_open_items_resolved_trip_id_fkey(id, project_number),
  opened_by:users!property_open_items_opened_by_user_id_fkey(full_name),
  resolved_by:users!property_open_items_resolved_by_user_id_fkey(full_name)
`;

export const openItemsKey = (locationId: string) => ['admin-open-items', locationId] as const;

export function useOpenItems(locationId: string | null | undefined) {
  return useQuery({
    queryKey: openItemsKey(locationId ?? ''),
    enabled: Boolean(locationId),
    queryFn: async () => {
      const { data, error } = await db
        .from('property_open_items')
        .select(OPEN_ITEM_COLUMNS)
        .eq('location_id', locationId!)
        .order('opened_at', { ascending: false })
        .limit(200);
      rethrow(error);
      return (data ?? []) as OpenItem[];
    },
    staleTime: 30_000,
  });
}

function useInvalidateOpenItems() {
  const qc = useQueryClient();
  return (locationId: string) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: openItemsKey(locationId) }),
      qc.invalidateQueries({ queryKey: RUNS_KEY }),
    ]);
}

export function useAddOpenItem() {
  const invalidate = useInvalidateOpenItems();
  return useMutation({
    mutationFn: async (args: { locationId: string; title: string; detail: string; priority: OpenItem['priority']; tripId: string | null }) => {
      const { error } = await db.from('property_open_items').insert({
        // customer_id is filled from the location by the guard trigger.
        location_id: args.locationId,
        title: args.title,
        detail: args.detail || null,
        priority: args.priority,
        opened_trip_id: args.tripId,
      });
      rethrow(error);
    },
    onSuccess: (_d, args) => invalidate(args.locationId),
  });
}

export function useCloseOpenItem() {
  const invalidate = useInvalidateOpenItems();
  return useMutation({
    mutationFn: async (args: { item: Pick<OpenItem, 'id' | 'location_id'>; status: 'resolved' | 'wont_fix' | 'open'; resolution: string; tripId: string | null }) => {
      const { error } = await db
        .from('property_open_items')
        .update({
          status: args.status,
          resolution: args.status === 'open' ? null : args.resolution,
          resolved_trip_id: args.status === 'open' ? null : args.tripId,
        })
        .eq('id', args.item.id);
      rethrow(error);
    },
    onSuccess: (_d, args) => invalidate(args.item.location_id),
  });
}

// ─── Links to Zoho ───────────────────────────────────────────────────────────

export interface TripLink {
  id: string;
  system: 'zoho_crm_deal' | 'zoho_books_invoice' | 'zoho_projects' | 'zoho_desk_ticket';
  external_id: string;
  label: string | null;
  url: string | null;
}

export function useTripLinks(tripId: string | null) {
  return useQuery({
    queryKey: ['admin-trip-links', tripId],
    enabled: Boolean(tripId),
    queryFn: async () => {
      const { data, error } = await db
        .from('trip_external_links')
        .select('id, system, external_id, label, url')
        .eq('trip_id', tripId!);
      rethrow(error);
      return (data ?? []) as TripLink[];
    },
    staleTime: 5 * 60_000,
  });
}

// ─── Deal import review ──────────────────────────────────────────────────────

export type CandidateStatus = 'ready' | 'needs_review' | 'imported' | 'ignored' | 'skipped';

export interface DealCandidate {
  zoho_deal_id: string;
  deal_name: string;
  stage: string | null;
  closing_date: string | null;
  amount: number | string | null;
  account_name: string | null;
  customer_id: string | null;
  location_id: string | null;
  visit_start: string | null;
  visit_end: string | null;
  status: CandidateStatus;
  reasons: string[];
  trip_id: string | null;
  /** An existing trip at the same property on overlapping dates. */
  duplicate_of_trip_id: string | null;
  reviewed_at: string | null;
  updated_at: string;
}

export const CANDIDATES_KEY = ['admin-deal-trip-candidates'] as const;

export function useDealCandidates() {
  return useQuery({
    queryKey: CANDIDATES_KEY,
    queryFn: async () => {
      const { data, error } = await db
        .from('zoho_deal_trip_candidates')
        .select('zoho_deal_id, deal_name, stage, closing_date, amount, account_name, customer_id, location_id, visit_start, visit_end, status, reasons, trip_id, duplicate_of_trip_id, reviewed_at, updated_at')
        .order('closing_date', { ascending: false, nullsFirst: false });
      rethrow(error);
      return (data ?? []) as DealCandidate[];
    },
    staleTime: 60_000,
  });
}

export function useResolveCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      dealId: string;
      action: 'import' | 'ignore' | 'reopen' | 'link';
      locationId?: string | null;
      visitStart?: string | null;
      visitEnd?: string | null;
      tripId?: string | null;
    }) => {
      const { data, error } = await db.rpc('resolve_deal_trip_candidate', {
        p_deal_id: args.dealId,
        p_action: args.action,
        p_location_id: args.locationId ?? null,
        p_visit_start: args.visitStart ?? null,
        p_visit_end: args.visitEnd ?? null,
        p_trip_id: args.tripId ?? null,
      });
      rethrow(error);
      return data as string | null;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: CANDIDATES_KEY }),
        qc.invalidateQueries({ queryKey: ['admin-field-trips'] }),
        qc.invalidateQueries({ queryKey: ['admin-trip-billing'] }),
      ]),
  });
}

// ─── Trip Atlas extras: open items, linked deals and money per trip ──────────

export interface AtlasDealLink {
  label: string | null;
  url: string | null;
  externalId: string;
}

interface TripFinancialsRow {
  trip_id: string;
  deal_count: number;
  revenue: number | string | null;
  cost: number | string | null;
  read_from_zoho: boolean;
}

const num = (v: number | string | null) => (v === null ? null : Number(v));

export function useAtlasExtras() {
  return useQuery({
    queryKey: ['admin-trip-atlas-extras'],
    queryFn: async () => {
      const [items, links, money] = await Promise.all([
        db.from('property_open_items').select('location_id').eq('status', 'open'),
        db.from('trip_external_links').select('trip_id, label, system, url, external_id'),
        db.from('trip_financials').select('trip_id, deal_count, revenue, cost, read_from_zoho'),
      ]);
      rethrow(items.error);
      rethrow(links.error);
      rethrow(money.error);
      const openByLocation = new Map<string, number>();
      for (const r of (items.data ?? []) as { location_id: string }[]) {
        openByLocation.set(r.location_id, (openByLocation.get(r.location_id) ?? 0) + 1);
      }
      const dealLabels = new Map<string, string[]>();
      const dealsByTrip = new Map<string, AtlasDealLink[]>();
      for (const r of (links.data ?? []) as { trip_id: string; label: string | null; system: string; url: string | null; external_id: string }[]) {
        if (r.system !== 'zoho_crm_deal') continue;
        dealsByTrip.set(r.trip_id, [...(dealsByTrip.get(r.trip_id) ?? []), { label: r.label, url: r.url, externalId: r.external_id }]);
        if (r.label) dealLabels.set(r.trip_id, [...(dealLabels.get(r.trip_id) ?? []), r.label]);
      }
      const financeByTrip = new Map<string, TripFinance>();
      for (const r of (money.data ?? []) as TripFinancialsRow[]) {
        financeByTrip.set(r.trip_id, { dealCount: r.deal_count, revenue: num(r.revenue), cost: num(r.cost), readFromZoho: r.read_from_zoho });
      }
      return { openByLocation, dealLabels, dealsByTrip, financeByTrip, fetchedAt: new Date().toISOString() };
    },
    staleTime: 60_000,
  });
}
